import { Link, useParams } from "react-router-dom";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import API, { BACKEND_URL } from "../api/api";
import SignaturePad from "../components/SignaturePad";
import {
  confirmAdminReservationCheckout,
  getAdminReservationCheckout
} from "../api/adminCheckouts";

const imageBase = BACKEND_URL;

const formatDate = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("nl-BE");
};

const parseList = (value) => {
  try {
    const parsed = JSON.parse(value || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export default function AdminCheckoutStart() {
  const { reservationId } = useParams();
  const queryClient = useQueryClient();
  const [notes, setNotes] = useState("");
  const [photos, setPhotos] = useState({});
  const [signatureDataUrl, setSignatureDataUrl] = useState("");
  const [signatureError, setSignatureError] = useState("");
  const [confirmed, setConfirmed] = useState(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin", "checkoutReservation", reservationId],
    queryFn: () => getAdminReservationCheckout(reservationId)
  });

  const checklist = useMemo(() => data?.requiredPhotoChecklist || [], [data]);
  const missingItems = useMemo(() => (
    checklist.filter((item) => !photos[item.type]?.imageUrl)
  ), [checklist, photos]);

  const uploadMutation = useMutation({
    mutationFn: async ({ item, file }) => {
      const formData = new FormData();
      formData.append("image", file);
      formData.append("type", item.type);
      const res = await API.post(`/upload/${reservationId}`, formData);
      return { item, imageUrl: res.data.filePath };
    },
    onSuccess: ({ item, imageUrl }) => {
      setPhotos((current) => ({
        ...current,
        [item.type]: {
          type: item.type,
          label: item.label,
          imageUrl
        }
      }));
      toast.success(`${item.label} toegevoegd`);
    },
    onError: () => toast.error("Foto uploaden mislukt")
  });

  const confirmMutation = useMutation({
    mutationFn: () => confirmAdminReservationCheckout(reservationId, {
      notes,
      photos: Object.values(photos),
      signatureDataUrl
    }),
    onSuccess: (result) => {
      setConfirmed(result);
      queryClient.invalidateQueries({ queryKey: ["admin", "checkouts"] });
      queryClient.invalidateQueries({ queryKey: ["reservations"] });
      queryClient.invalidateQueries({ queryKey: ["trailers"] });
      toast.success("Check-out bevestigd");
    },
    onError: (error) => toast.error(error.response?.data?.error || "Check-out bevestigen mislukt")
  });

  const submitCheckout = () => {
    if (!signatureDataUrl) {
      setSignatureError("Handtekening is verplicht.");
      toast.error("Plaats eerst een handtekening.");
      return;
    }
    confirmMutation.mutate();
  };

  if (isLoading) return <div className="loading-state">Check-out laden...</div>;

  if (isError || !data) {
    return (
      <section className="page">
        <div className="panel panel-pad">
          <h1 className="page-title">Check-out niet gevonden</h1>
          <Link className="button secondary" to="/admin/checkouts">Terug naar check-outs</Link>
        </div>
      </section>
    );
  }

  if (data.checkoutInspection) {
    return (
      <section className="page">
        <div className="panel panel-pad">
          <h1 className="page-title">Check-out bestaat al</h1>
          <p className="page-subtitle">Voor deze reservatie is al een check-out bevestigd.</p>
          <Link className="button secondary" to={`/admin/checkouts/${data.checkoutInspection.id}`}>Bekijk checkout</Link>
        </div>
      </section>
    );
  }

  if (confirmed) {
    return (
      <section className="page">
        <div className="panel panel-pad checkout-success">
          <h1 className="page-title">Check-out bevestigd</h1>
          <p className="page-subtitle">De foto's en opmerkingen zijn opgeslagen. De reservatie staat nu op uitgecheckt.</p>
          <div className="action-row">
            <Link className="button" to={`/admin/checkouts/${confirmed.inspection.id}`}>Bekijk checkout</Link>
            <Link className="button secondary" to="/admin/checkouts">Terug naar overzicht</Link>
          </div>
        </div>
      </section>
    );
  }

  const { reservation, trailer, customer, requestedOptions = [], existingDamageCases = [] } = data;

  return (
    <section className="page checkout-mobile admin-checkout-page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Checkout uitvoeren</h1>
          <p className="page-subtitle">Manuele controle voor reservatie #{reservation.id}</p>
        </div>
        <Link className="button secondary" to="/admin/checkouts">Terug</Link>
      </header>

      <section className="panel panel-pad">
        <h2 className="detail-title">Reservatie</h2>
        <div className="detail-grid">
          <div className="detail-item"><div className="detail-label">Periode</div><div className="detail-value">{formatDate(reservation.startDate)} tot {formatDate(reservation.endDate)}</div></div>
          <div className="detail-item"><div className="detail-label">Klant</div><div className="detail-value">{`${customer?.firstName || reservation.firstName || ""} ${customer?.lastName || reservation.name || ""}`.trim() || "-"}</div></div>
          <div className="detail-item"><div className="detail-label">Status</div><div className="detail-value">{reservation.status}</div></div>
          <div className="detail-item"><div className="detail-label">Trailer</div><div className="detail-value">{trailer?.name || "-"}</div></div>
        </div>
      </section>

      <section className="panel panel-pad">
        <h2 className="detail-title">Aangevraagde opties</h2>
        {requestedOptions.length ? (
          <div className="status-line">
            {requestedOptions.map((option) => <span key={option} className="badge info">{option}</span>)}
          </div>
        ) : (
          <div className="empty-state">Geen opties opgegeven.</div>
        )}
      </section>

      <section className="panel panel-pad">
        <h2 className="detail-title">Bekende schade</h2>
        {existingDamageCases.length === 0 && <div className="empty-state">Geen openstaande schadegevallen gekend.</div>}
        {existingDamageCases.map((damageCase) => {
          const damagePhotos = parseList(damageCase.photos);
          return (
            <div key={damageCase.id} className="checkout-damage">
              <div className="status-line">
                <span className="badge danger">{damageCase.status}</span>
                <span className="badge warning">EUR {damageCase.damageCost || 0}</span>
              </div>
              <p className="page-subtitle">{damageCase.description || "Geen omschrijving"}</p>
              {damagePhotos.length > 0 && (
                <div className="photo-grid">
                  {damagePhotos.map((photo) => (
                    <img key={photo} className="photo-thumb" src={`${imageBase}${photo}`} alt="Gekende schade" />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </section>

      <section className="panel panel-pad">
        <h2 className="detail-title">Foto-checklist</h2>
        <div className="checkout-checklist">
          {checklist.map((item) => {
            const photo = photos[item.type];
            const inputId = `admin-checkout-photo-${item.type}`;

            return (
              <div key={item.type} className={`checkout-photo-item${photo ? " complete" : ""}`}>
                <div className="checkout-photo-copy">
                  <span className="detail-label">{item.option ? "Optie/accessoire" : "Trailerpositie"}</span>
                  <strong>{item.label}</strong>
                  <span className={`checkout-photo-status${photo ? " complete" : ""}`}>
                    {photo ? "Foto toegevoegd" : "Nog niet toegevoegd"}
                  </span>
                </div>

                {photo ? (
                  <img className="checkout-photo-preview" src={`${imageBase}${photo.imageUrl}`} alt={item.label} />
                ) : (
                  <div className="checkout-photo-placeholder">Geen foto</div>
                )}

                <input
                  id={inputId}
                  className="checkout-file-input"
                  type="file"
                  accept="image/jpeg,image/png"
                  capture="environment"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) uploadMutation.mutate({ item, file });
                    event.target.value = "";
                  }}
                />
                <label className="button checkout-camera-button" htmlFor={inputId}>
                  {photo ? "Foto vervangen" : "Foto nemen"}
                </label>
                <p className="row-meta checkout-photo-hint">Camera op gsm, galerij als fallback.</p>
              </div>
            );
          })}
        </div>
        {missingItems.length > 0 && (
          <p className="error-text">Nog ontbrekend: {missingItems.map((item) => item.label).join(", ")}</p>
        )}
      </section>

      <section className="panel panel-pad">
        <label className="field">
          <span className="field-label">Opmerkingen</span>
          <textarea className="textarea" value={notes} onChange={(event) => setNotes(event.target.value)} />
        </label>
      </section>

      <section className="panel panel-pad">
        <SignaturePad
          label="Digitale handtekening"
          confirmationText="Ik bevestig dat ik de aanhangwagen in bovenstaande staat terugbezorg."
          value={signatureDataUrl}
          onChange={(nextSignature) => {
            setSignatureDataUrl(nextSignature);
            if (nextSignature) setSignatureError("");
          }}
          error={signatureError}
        />
      </section>

      <div className="checkout-submit-bar">
        <div>
          <strong>{missingItems.length === 0 && signatureDataUrl ? "Alles klaar" : "Controle ontbreekt"}</strong>
          <span>{uploadMutation.isPending ? "Foto uploaden..." : "Controleer foto's en handtekening voor bevestiging."}</span>
        </div>
        <button
          className="button checkout-submit-button"
          disabled={missingItems.length > 0 || confirmMutation.isPending || uploadMutation.isPending}
          onClick={submitCheckout}
        >
          {confirmMutation.isPending ? "Bevestigen..." : "Check-out bevestigen"}
        </button>
      </div>
    </section>
  );
}
